import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  StyleSheet,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { useGetTagsQuery, useGetThreadQuery } from '@/store/api/corkBoard/corkBoardApi';
import { useThreadForm, ThreadFormInitialData } from '@/hooks/residents/corkBoard/useThreadForm';
import { CreateThreadForm } from '@/components/residents/corkBoard/CreateThreadForm';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type EditThreadRouteProp = RouteProp<RootStackParamList, 'EditThread'>;
type NavigationProp = StackNavigationProp<RootStackParamList, 'EditThread'>;

export const EditThreadScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<EditThreadRouteProp>();
  const { threadId } = route.params;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;

  const isDesktop = layoutMode === 'desktop';

  const [isDiscardVisible, setIsDiscardVisible] = useState(false);

  // Fetch existing thread data
  const { data: threadData, isLoading: isLoadingThread, isError } = useGetThreadQuery({
    threadId,
    page: 0,
    pageSize: 1,
  });

  // Fetch tags for the property
  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const initialData = useMemo<ThreadFormInitialData | undefined>(() => {
    if (!threadData?.thread) return undefined;
    const thread = threadData.thread;
    return {
      title: thread.title,
      body: thread.body,
      tagIds: thread.tags.map((t) => Number(t.id)),
      attachments: thread.attachments,
    };
  }, [threadData?.thread]);

  const handleSuccess = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const form = useThreadForm({
    mode: 'edit',
    threadId,
    initialData,
    onSuccess: handleSuccess,
  });

  const handleClose = useCallback(() => {
    if (form.isDirty) {
      setIsDiscardVisible(true);
    } else {
      navigation.goBack();
    }
  }, [form.isDirty, navigation]);

  const handleDiscard = useCallback(() => {
    setIsDiscardVisible(false);
    navigation.goBack();
  }, [navigation]);

  const isDataLoading = isLoadingThread || isLoadingTags;
  const canSave = form.isValid && form.isDirty && !form.isSubmitting;

  // Error fetching thread
  if (isError && !threadData) {
    return (
      <View style={[styles.screenContainer, !isDesktop && Platform.OS !== 'ios' && { paddingTop: insets.top }]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.headerSide}
            onPress={() => navigation.goBack()}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={SEMANTIC_COLORS.text.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Edit Post</Text>
          <View style={styles.headerSide} />
        </View>
        <View style={styles.centeredContainer}>
          <Text style={styles.errorText}>Failed to load thread</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.retryButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.screenContainer,
        isDesktop && styles.desktopOverlay,
      ]}
    >
      {isDesktop && (
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={handleClose}
        />
      )}

      <View
        style={[
          styles.card,
          isDesktop && styles.desktopCard,
          !isDesktop && Platform.OS !== 'ios' && { paddingTop: insets.top },
        ]}
      >
        {!isDesktop && <StatusBar barStyle="light-content" />}

        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={0}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.headerSide}
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={SEMANTIC_COLORS.text.primary} />
            </TouchableOpacity>

            <Text style={styles.headerTitle}>Edit Post</Text>

            <TouchableOpacity
              style={[styles.headerSide, styles.saveButton, !canSave && styles.saveButtonDisabled]}
              onPress={form.handleSubmit}
              disabled={!canSave}
              activeOpacity={0.7}
            >
              {form.isSubmitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>
                  Save
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Form */}
          {isDataLoading || !initialData ? (
            <View style={styles.centeredContainer}>
              <ActivityIndicator size="large" color={SEMANTIC_COLORS.interactive.primary} />
            </View>
          ) : (
            <CreateThreadForm
              mode="edit"
              tags={tags}
              title={form.title}
              onChangeTitle={form.setTitle}
              body={form.body}
              onChangeBody={form.setBody}
              selectedTagIds={form.selectedTagIds}
              onToggleTag={form.toggleTag}
              attachments={form.attachments}
              existingAttachments={form.existingAttachments}
              onAddFiles={form.addFiles}
              onRemoveFile={form.removeFile}
              onRemoveExistingAttachment={form.removeExistingAttachment}
              errors={form.errors}
              hasAttemptedSubmit={form.hasAttemptedSubmit}
              isSubmitting={form.isSubmitting}
              submitError={form.submitError}
            />
          )}
        </KeyboardAvoidingView>
      </View>

      <ConfirmationModal
        visible={isDiscardVisible}
        message="You have unsaved changes. Are you sure you want to discard your edits?"
        onCancel={() => setIsDiscardVisible(false)}
        onProceed={handleDiscard}
        cancelText="Keep Editing"
        proceedText="Discard"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  desktopOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  desktopCard: {
    flex: 0,
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 640,
    minHeight: 480,
    maxHeight: 720,
    borderRadius: 16,
    overflow: 'hidden',
  },
  flex: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray800,
  },
  headerSide: {
    minWidth: 60,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    textAlign: 'center',
    flex: 1,
  },
  saveButton: {
    backgroundColor: SEMANTIC_COLORS.interactive.primary,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    fontSize: 16,
    color: SEMANTIC_COLORS.text.secondary,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: SEMANTIC_COLORS.interactive.primary,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
